export class HttpError extends Error {
    readonly status: number;
    readonly statusText: string;
    readonly body: string;
    readonly url: string;

    constructor(status: number, statusText: string, body: string, url: string) {
        super(`HTTP ${status} ${statusText} for ${url}`);
        this.name = 'HttpError';
        this.status = status;
        this.statusText = statusText;
        this.body = body;
        this.url = url;
    }
}

type ParamValue = string | number | boolean;

export interface FetchOptions extends RequestInit {
    json?: unknown;
    params?: Record<string, ParamValue>;
}

const buildUrl = (url: string, params?: Record<string, ParamValue>): string => {
    if (!params) return url;
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        search.append(key, String(value));
    }
    const query = search.toString();
    if (!query) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${query}`;
};

const buildInit = (options: FetchOptions): RequestInit => {
    const { json, params: _params, headers, body, ...rest } = options;
    if (json !== undefined) {
        const merged = new Headers(headers);
        if (!merged.has('Content-Type')) {
            merged.set('Content-Type', 'application/json');
        }
        return { ...rest, headers: merged, body: JSON.stringify(json) };
    }
    return { ...rest, headers, body };
};

const send = async (url: string, options: FetchOptions): Promise<Response> => {
    const finalUrl = buildUrl(url, options.params);
    const response = await fetch(finalUrl, buildInit(options));
    if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new HttpError(response.status, response.statusText, body, finalUrl);
    }
    return response;
};

export const fetchJson = async <T>(url: string, options: FetchOptions = {}): Promise<T> => {
    const response = await send(url, options);
    return (await response.json()) as T;
};

export const fetchVoid = async (url: string, options: FetchOptions = {}): Promise<void> => {
    await send(url, options);
};
